import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDots, CaretLeft, CaretRight, ListBullets, Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { DateRangePicker, PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui-kit/toggle-group';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { DEFAULT_MY_TIMESHEETS_ORDER } from '../../graphql/queries';
import {
  createTimesheetTableColumns,
  timesheetTotals,
} from '../../components/timesheet-table-columns/timesheet-table-columns';
import { TimesheetTaskFormDialog } from '../../components/timesheet-task-form-dialog/timesheet-task-form-dialog';
import { TimesheetWeekView } from '../../components/timesheet-week-view/timesheet-week-view';
import {
  useCurrentUserId,
  useDeleteTimeSheetEntry,
  useGetMyTimeSheets,
} from '../../hooks/use-my-timesheet';
import { useDayTarget } from '../../hooks/use-under-time';
import { TimesheetView, useTimesheetView } from '../../hooks/use-timesheet-view';
import { TimeSheetRecord, TimeSheetsPagingResult } from '../../types/timesheet.types';
import {
  addDaysToDay,
  orgToday,
  startOfMonthDay,
  startOfWeekDay,
  taskDateRangeClause,
} from '../../../utils/task-date';

const timeSheetsOf = (data: unknown) => (data as { TimeSheets?: TimeSheetsPagingResult })?.TimeSheets;

/**
 * Time Tracker (HR Module Prototype, Component Map › Time Tracker): opens on the Week view (seven day
 * columns from a first day, stepped a week at a time) with a Week / List switch remembered in this
 * browser; the List keeps its from–to range and the table.
 */
export const MyTimesheetPage = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const dayTarget = useDayTarget();
  const [view, setView] = useTimesheetView();
  const [today] = useState(orgToday);

  const [weekStart, setWeekStart] = useState(() => startOfWeekDay(today));
  const weekEnd = addDaysToDay(weekStart, 6);
  const [dateRange, setDateRange] = useState(() => ({ from: startOfMonthDay(today), to: today }));
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 25,
    defaultOrder: DEFAULT_MY_TIMESHEETS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; timeSheet: TimeSheetRecord | null }>({
    open: false,
    timeSheet: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<TimeSheetRecord | null>(null);

  const filter = {
    UserId: userId,
    ...(dateRange.from && dateRange.to
      ? { TaskDate: taskDateRangeClause(dateRange.from, dateRange.to) }
      : {}),
  };

  // A new date range starts over from the first page.
  useEffect(() => {
    resetPage();
  }, [dateRange, resetPage]);

  const list = useGetMyTimeSheets(
    {
      pageNo: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filter: mergeWhere(filter, columnWhere),
      order,
    },
    view === 'list'
  );
  const listResult = timeSheetsOf(list.data);
  // The totals row covers the whole range, not just the page shown (one entry per day keeps it small).
  const listAll = useGetMyTimeSheets(
    { pageNo: 1, pageSize: 500, filter: mergeWhere(filter, columnWhere), order },
    view === 'list'
  );
  const totals = timesheetTotals(timeSheetsOf(listAll.data)?.items ?? []);

  // One entry per day, so a week is at most seven; the extra room covers stray duplicates.
  const week = useGetMyTimeSheets(
    {
      pageNo: 1,
      pageSize: 50,
      filter: { UserId: userId, TaskDate: taskDateRangeClause(weekStart, weekEnd) },
      order: DEFAULT_MY_TIMESHEETS_ORDER,
    },
    view === 'week'
  );

  const { mutateAsync: deleteEntry, isPending: isDeleting } = useDeleteTimeSheetEntry();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteEntry(deleteTarget.ItemId);
    setDeleteTarget(null);
  };

  const openEdit = (record: TimeSheetRecord) => setFormState({ open: true, timeSheet: record });

  const columns = createTimesheetTableColumns({
    t,
    dayTarget,
    onEdit: openEdit,
    onDelete: setDeleteTarget,
  });

  const weekPicker = (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-9 shrink-0"
        aria-label={t('MY_TIMESHEET_PREVIOUS_WEEK')}
        title={t('MY_TIMESHEET_PREVIOUS_WEEK')}
        onClick={() => setWeekStart((day) => addDaysToDay(day, -7))}
      >
        <CaretLeft className="size-4" />
      </Button>
      <DatePicker value={weekStart} onChange={(value) => value && setWeekStart(value)} />
      <span className="shrink-0 whitespace-nowrap text-sm text-muted-foreground">
        {t('MY_TIMESHEET_TO')} {formatDateOnly(weekEnd)}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-9 shrink-0"
        aria-label={t('MY_TIMESHEET_NEXT_WEEK')}
        title={t('MY_TIMESHEET_NEXT_WEEK')}
        onClick={() => setWeekStart((day) => addDaysToDay(day, 7))}
      >
        <CaretRight className="size-4" />
      </Button>
    </div>
  );

  const rangePicker = (
    <DateRangePicker value={dateRange} onChange={setDateRange} aria-label={t('DATE_RANGE')} />
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('TIME_TRACKER')}
        actions={[
          {
            label: t('MY_TIMESHEET_ADD_TASK'),
            icon: <Plus className="size-5" />,
            onClick: () => setFormState({ open: true, timeSheet: null }),
          },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        {view === 'week' ? weekPicker : rangePicker}
        <ToggleGroup
          type="single"
          variant="outline"
          size="icon"
          value={view}
          onValueChange={(value) => value && setView(value as TimesheetView)}
          aria-label={t('MY_TIMESHEET_VIEW')}
        >
          <ToggleGroupItem
            value="week"
            aria-label={t('MY_TIMESHEET_WEEK_VIEW')}
            title={t('MY_TIMESHEET_WEEK_VIEW')}
          >
            <CalendarDots />
          </ToggleGroupItem>
          <ToggleGroupItem
            value="list"
            aria-label={t('MY_TIMESHEET_LIST_VIEW')}
            title={t('MY_TIMESHEET_LIST_VIEW')}
          >
            <ListBullets />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {view === 'week' ? (
        <TimesheetWeekView
          startDay={weekStart}
          records={timeSheetsOf(week.data)?.items ?? []}
          today={today}
          isLoading={week.isLoading}
          dayTarget={dayTarget}
          onEdit={openEdit}
          onDelete={setDeleteTarget}
        />
      ) : (
        <AdvanceDataTable
          {...tableProps}
          data={listResult?.items ?? []}
          columns={columns}
          isLoading={list.isLoading}
          error={list.error instanceof Error ? list.error : null}
          pagination={{
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            totalCount: listResult?.totalCount ?? 0,
          }}
          isExpandRowContent={false}
          mobileView="cards"
          totals={totals}
        />
      )}

      <TimesheetTaskFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, timeSheet: open ? prev.timeSheet : null }))
        }
        timeSheet={formState.timeSheet}
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('MY_TIMESHEET_DELETE_ENTRY')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        confirmClassName="bg-destructive hover:bg-destructive-hover"
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
